import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { AuthService } from "./auth.service";
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { User, UserAPI, UserList, UserListAPI } from '../models/user';
import { Qualification, QualificationAPI } from "../models/qualifications";
import { PatientList, PatientListAPI } from '../models/patient';

type RetourAPI = {
  data: UserAPI;
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  getHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getToken(),
    });
  }

  pullList(
    page?: number | undefined,
    per_page?: number,
    nom?: string,
    role_id?: number,
  ): Observable<UserList> {
    const headers = this.getHeaders();

    var params: HttpParams = new HttpParams();

    page ? (params = params.set('page', page)) : null;

    per_page ? (params = params.set('per_page', per_page)) : null;

    nom ? (params = params.set('q', nom)) : null;

    role_id ? (params = params.set('role_id', role_id)) : null;

    return this.http
      .get<UserListAPI>(`${environment.api.gateway}user`, {
        headers,
        params: params,
      })
      .pipe(map((userListAPI: UserListAPI) => UserList.factory(userListAPI)));
  }

  pullDetail(id: number | undefined): Observable<User> {
    const headers = this.getHeaders();

    return this.http
      .get<RetourAPI>(`${environment.api.gateway}user/${id}`, {
        headers,
      })
      .pipe(map((retourAPI: RetourAPI) => User.factory(retourAPI.data)));
  }

  push(user: User): Observable<any> {
    return this.http.put(
      `${environment.api.gateway}user/${user.id}`,
      user.toAPI(),
      {
        headers: this.getHeaders(),
      },
    );
  }

  updatePwd(id: number, data: any): Observable<any> {
    return this.http.put(
      `${environment.api.gateway}user/updatepwd/${id}`,
      data,
      { headers: this.getHeaders() },
    );
  }

  synchronisationDesUtilisateurs(): Observable<any> {
    return this.http.get(`${environment.api.gateway}sync/medecins`, {
      headers: this.getHeaders(),
    });
  }

  pullQualifications(): Observable<Qualification[]> {
    const headers = this.getHeaders();
    type RetourAPI = {
      data: QualificationAPI[];
    };

    return this.http
      .get<RetourAPI>(`${environment.api.gateway}list/qualifications`, {
        headers,
      })
      .pipe(map((retourAPI: RetourAPI) => retourAPI.data));
  }

  pullPatientList(
    user_id: number,
    page: number | undefined = undefined,
    nom: string | undefined,
    dosActiviteList: string[] = [],
    dosEcheanceMin: Date | undefined = undefined,
    dosEcheanceMax: Date | undefined = undefined,
    dosEcheanceInitiales: boolean = false,
  ): Observable<PatientList> {
    const headers = this.getHeaders();

    var params: HttpParams = new HttpParams();

    page ? (params = params.set('page', page)) : null;

    params = params.set('per_page', 15);

    nom ? (params = params.set('q', nom)) : null;

    for (let index = 0; index < dosActiviteList.length; index++) {
      const dosActivite = dosActiviteList[index];

      params = params.set('dos_activite[' + index + ']', dosActivite);
    }

    if (dosEcheanceMin) {
      const year = dosEcheanceMin.getFullYear();
      const month = String(dosEcheanceMin.getMonth() + 1).padStart(2, '0');
      const day = String(dosEcheanceMin.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      params = params.set('date_echeance_min', formattedDate);
    }

    if (dosEcheanceMax) {
      const year = dosEcheanceMax.getFullYear();
      const month = String(dosEcheanceMax.getMonth() + 1).padStart(2, '0');
      const day = String(dosEcheanceMax.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      params = params.set('date_echeance_max', formattedDate);
    }

    dosEcheanceInitiales ? (params = params.set('echeance_initiale', 1)) : null;

    return this.http
      .get<PatientListAPI>(
        `${environment.api.gateway}medecins/${user_id}/patients`,
        {
          headers: headers,
          params: params,
        },
      )
      .pipe(
        map((patientListAPI: PatientListAPI) =>
          PatientList.factory(patientListAPI),
        ),
      );
  }
}
